import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import * as React from 'react'

import { cn } from '@/lib/utils'

const buttonShadow = '0_10px_30px_-10px_hsl(var(--primary)_/_0.4)'

const buttonVariants = cva(
  'inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-full font-medium text-sm ring-offset-background transition-[background-color,color,box-shadow,opacity,border-color] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default: `bg-primary text-primary-foreground not-disabled:hover:bg-primary-dark not-disabled:hover:shadow-[${buttonShadow}]`,
        destructive:
          'bg-destructive text-destructive-foreground not-disabled:hover:bg-destructive/90',
        outline:
          'border border-input bg-transparent not-disabled:hover:bg-accent not-disabled:hover:text-accent-foreground',
        'outline-dotted':
          'rounded-md border border-input border-dashed bg-transparent px-3 text-muted-foreground not-disabled:hover:bg-accent not-disabled:hover:text-accent-foreground',
        'outline-subtle':
          'border border-border bg-transparent text-muted-foreground not-disabled:hover:border-border-hover not-disabled:hover:bg-muted not-disabled:hover:text-foreground',
        secondary:
          'bg-secondary text-secondary-foreground not-disabled:hover:bg-secondary/80',
        ghost:
          'not-disabled:hover:bg-accent not-disabled:hover:text-accent-foreground',
        link: 'text-primary underline-offset-4 not-disabled:hover:underline',
        'link-underline': 'text-muted-foreground underline decoration-dotted',
        black:
          'bg-foreground text-background not-disabled:hover:shadow-[0_8px_24px_-8px_hsl(var(--foreground)_/_0.35)]',
        purple:
          'border-status-experiment/30 bg-status-experiment-surface text-status-experiment-foreground',
        stella: cn(
          'bg-linear-[135deg] from-[hsl(var(--stella-gradient-from))] via-[hsl(var(--stella-gradient-via))] to-[hsl(var(--stella-gradient-to))] transition',
          'text-primary-foreground shadow-[inset_0_1px_0_hsl(var(--highlight)_/_0.3),0_8px_22px_-8px_hsl(var(--stella-gradient-from)_/_0.45),0_2px_6px_hsl(var(--stella-gradient-from)_/_0.18)]',
          'hover:-translate-y-px hover:shadow-[inset_0_1px_0_hsl(var(--highlight)_/_0.3),0_12px_28px_-8px_hsl(var(--stella-gradient-from)_/_0.55),0_3px_10px_hsl(var(--stella-gradient-from)_/_0.22)]'
        ),
        surface: 'border border-border bg-card text-foreground',
        white:
          'border border-border bg-card text-foreground not-disabled:hover:border-border-hover',
        'destructive-outline':
          'border text-destructive-text not-disabled:hover:border-destructive',
      },
      size: {
        default: 'h-10 px-4 py-2',
        xs: 'h-7 px-2.5 py-1 text-xs',
        sm: 'h-9 px-3',
        lg: 'h-11 px-8',
        icon: 'size-10',
        'md-icon': 'size-7',
        'sm-icon': 'size-4',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button'
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = 'Button'

export { Button, buttonVariants }
